import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useThemeColor } from '../hooks/useThemeColor'
import { useTranslation } from '../i18n/LanguageContext'
import Users from './Users'
import Organization from './settings/Organization'
import Language from './settings/Language'
import ManageTasks from './settings/ManageTasks'
import AddTasks from './settings/AddTasks'
import UsageHistory from './settings/UsageHistory'

const Settings = () => {
  const { aispaceColor } = useThemeColor()
  const { data } = useSelector((state) => state.admin)
  const { t } = useTranslation()

  const isSuperAdmin = data?.user_role === 'SUPER_ADMIN'
  const isAdmin = data?.user_role === 'ADMIN'

  // SUPER_ADMIN: all tabs. ADMIN: administrator + organization (org-scoped) + manage-tasks + language
  const tabs = isSuperAdmin
    ? [
        { id: 'administrator',  label: t('settings.tabAdministrator') },
        { id: 'organization',   label: t('settings.tabOrganization')  },
        { id: 'usage-history',  label: t('usageHistory.title')         },
        { id: 'language',       label: t('settings.tabLanguage')      },
      ]
    : [
        { id: 'administrator', label: t('settings.tabAdministrator') },
        { id: 'organization',  label: t('settings.tabOrganization')  },
        { id: 'language',      label: t('settings.tabLanguage')      },
      ]

  const defaultTab = 'administrator'
  const [selectedTab, setSelectedTab] = useState(defaultTab)
  const [showAddTask, setShowAddTask] = useState(false)

  useEffect(() => {
    document.body.classList.add('block-scroll')
    return () => {
      document.body.classList.remove('block-scroll')
    }
  }, [])

  return (
    <div className="h-screen bg-white overflow-hidden flex flex-col">
      <header className="px-10 h-[80px] flex items-center flex-shrink-0">
        <h1 className="text-[22px] font-bold text-[#111827]">{t('settings.title')}</h1>
      </header>

      {/* Sticky Tabs — stays in place while content below scrolls */}
      <div className="flex-shrink-0 bg-white px-10 border-b border-[#E5E7EB]">
        {/* Tabs */}
        <div className="flex items-center gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setSelectedTab(tab.id)
                setShowAddTask(false) // Reset AddTask view when switching tabs
              }}
              className={`pb-3 text-[14px] font-medium transition-colors relative ${
                selectedTab !== tab.id ? 'text-[#6B7280] hover:text-[#374151]' : ''
              }`}
              style={selectedTab === tab.id ? { color: aispaceColor } : {}}
            >
              {tab.label}
              {selectedTab === tab.id && (
                <div
                  className="absolute -bottom-px left-0 right-0 h-[2px] rounded-full"
                  style={{ backgroundColor: aispaceColor }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable Tab Content */}
      <div className="flex-1 overflow-y-auto px-10 py-8">
        {selectedTab === 'manage-tasks' && !showAddTask && (
          <ManageTasks key="manage-tasks" setShowAddTask={setShowAddTask} />
        )}
        {selectedTab === 'manage-tasks' && showAddTask && (
          <AddTasks key="add-tasks" setShowAddTask={setShowAddTask} />
        )}
        {selectedTab === 'administrator' && <Users />}
        {selectedTab === 'organization' && (isSuperAdmin || isAdmin) && <Organization />}
        {selectedTab === 'usage-history' && <UsageHistory />}
        {selectedTab === 'language' && <Language />}
      </div>
    </div>
  )
}

export default Settings
